import type { AppRouter } from '@fieldops/api/src/trpc/routers/index.js'
import { QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client'
import type { inferRouterOutputs } from '@trpc/server'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import superjson from 'superjson'

/**
 * The API client.
 *
 * Types come straight from the server router, so a renamed field or a changed
 * input is a compile error here rather than a runtime surprise in front of a
 * customer.
 */

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>()

/**
 * What a procedure returns, for components that take a row as a prop and so
 * cannot infer it from the `useQuery` call site.
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>

/** Native builds talk to an absolute origin; the web build is same-origin. */
const apiUrl = import.meta.env['VITE_API_URL'] ?? ''

/**
 * Capacitor cannot rely on a first-party cookie, so the token is kept and
 * sent as a bearer header there. On the web the cookie does the work and this
 * is never populated.
 */
let bearerToken: string | null = localStorage.getItem('fieldops_token')

export function setBearerToken(token: string | null): void {
  bearerToken = token
  if (token) localStorage.setItem('fieldops_token', token)
  else localStorage.removeItem('fieldops_token')
}

/** For the file URLs, which cannot carry a header. Null on the web build. */
export function currentBearerToken(): string | null {
  return bearerToken
}

/** Where the API is, for the URLs a browser fetches itself rather than us. */
export const apiOrigin = apiUrl

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${apiUrl}/trpc`,
      transformer: superjson,
      fetch: (url, options) =>
        fetch(url, {
          ...options,
          // Sends the session cookie on the web build.
          credentials: 'include',
        }),
      headers: () => (bearerToken ? { authorization: `Bearer ${bearerToken}` } : {}),
    }),
  ],
})

export function isUnauthorised(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED'
}

export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!(error instanceof TRPCClientError)) return {}
  return (error.data?.fieldErrors as Record<string, string[]>) ?? {}
}

export function errorMessage(error: unknown): string {
  if (error instanceof TRPCClientError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Realtime invalidates precisely when something changes, so polling
        // and refetch-on-focus are redundant noise. Data is fresh because the
        // server said so, not because we asked again.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Re-requesting something you are not allowed to see will not start
          // working on the third attempt.
          if (isUnauthorised(error)) return false
          if (error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN') return false
          return failureCount < 2
        },
      },
      mutations: { retry: false },
    },
  })
}
